import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Backdrop, CircularProgress } from "@mui/material";
import { StyledForm } from "../../../components/Form/FormComponents.styled";
import { CustomSmallBtn } from "../Lease.styled";
import {
	AssetRegistrationInfo,
	CompletedAssetInformation,
	CompletedAssetProductInfo,
} from "../partials/FormFields";
import FormAccordion from "../../../components/Form/FormAccordion";
import { AssetRegistrationSideBar } from "../partials/RegistrationSideBar";
import { getCurrentUserInfo } from "../../../utils/UserAuth";
import { getInvestorEmail, getInvestorName, investorRegisterAsset } from "../utils";
import { getAssetRegPaymentStatus, onUpdateAsset, updateLogObject } from "../../logistics/utils";
import { walletType } from "../../dashboard/DashboardUtils";
import useParameters from "../../../utils/useParameters";
import { RegisterAssetModal } from "../partials/RegAndInsuranceModal";

const AssetRegistration = ({ leaseData }) => {
	const parameters = useParameters();
	const navigate = useNavigate();
	const currentAdmin = getCurrentUserInfo();
	const { adminWallet } = useSelector((state) => state.wallet);

	const mainWallet = adminWallet?.find((wallet) => wallet?.product === walletType.main);

	const vehicleFee = parameters?.find((p) => p?.parameter?.includes("Private vehicle registration"));

	const totalAmountToPay =
		process.env.NODE_ENV !== "development" ? Number(vehicleFee?.value?.replaceAll(",", "")) : 2;

	const [toggleEdit, setToggleEdit] = useState(false);

	const [loading, setLoading] = useState(false);

	const [regAssetModal, setRegAssetModal] = useState(false);
	const [activeTab, setActiveTab] = useState(1);

	const { isPaymentComplete, paymentStatus } = getAssetRegPaymentStatus(leaseData);

	const formData = {
		investorName: getInvestorName(leaseData),
		investorEmail: getInvestorEmail(leaseData),
		chassisNum: leaseData?.vehicleInfo?.chassisNum,
		engineNum: leaseData?.vehicleInfo?.engineNum,
		assetInvoice: leaseData?.vehicleInfo?.assetInvoice,
		vehicleMake: leaseData?.productObj?.brand,
		vehicleName: leaseData?.productObj?.productName,
	};

	const [miniForm, setMiniForm] = useState({
		registrationNum: leaseData?.vehicleRegDetails?.registrationNum || "",
		registrationStatus: leaseData?.vehicleRegDetails?.registrationStatus || "",
		paymentStatus,
	});

	const handleChange = (e) => setMiniForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

	const isAssetRegistrationReady = () => {
		if (
			currentAdmin?.privileges === "firstLineApproval" &&
			currentAdmin?.role?.toLowerCase() === "finance" &&
			leaseData?.leaseType === "Quick Lease" &&
			leaseData?.vehicleInfo &&
			!leaseData?.assetRegistration?.length
		) {
			return true;
		}
		if (
			currentAdmin?.privileges?.toLowerCase() === "super administrator" &&
			leaseData?.leaseType === "Quick Lease" &&
			leaseData?.vehicleInfo &&
			!leaseData?.assetRegistration?.length
		) {
			return true;
		} else {
			return false;
		}
	};

	const isSaveAssetRegNumberReady = () => {
		if (leaseData?.vehicleRegDetails?.registrationStatus === "Registered") {
			return false;
		}
		if (
			currentAdmin?.privileges === "firstLineApproval" &&
			currentAdmin?.role?.toLowerCase() === "finance" &&
			isPaymentComplete
		) {
			return true;
		}
		if (currentAdmin?.privileges?.toLowerCase() === "super administrator" && isPaymentComplete) {
			return true;
		} else {
			return false;
		}
	};

	const handleCancel = () => {
		setToggleEdit(false);
		setMiniForm({
			...miniForm,
			registrationStatus: leaseData?.vehicleRegDetails?.registrationStatus || "",
			registrationNum: leaseData?.vehicleRegDetails?.registrationNum || "",
		});
	};

	const onBeginAssetRegPayment = () => {
		if (mainWallet?.availableBalance < totalAmountToPay) {
			toast.error("Insuffient balance. Please fund your wallet!");
			return;
		}
		setRegAssetModal(true);
		setActiveTab(1);
	};

	const onAdminRegisterAsset = async () => {
		const leaseDataToUpdate = {
			totalAmountToPay,
			amountToPay: totalAmountToPay,
			leaseId: leaseData?.objectId,
			vehicleRegDetails: {
				...formData,
			},
		};

		const assetDataToUpdate = {
			assetRegistration: [
				...(leaseData?.assetRegistration || []),
				{
					name: `${currentAdmin?.firstName} ${currentAdmin?.lastName}`,
					userId: currentAdmin?.objectId,
					amountPaid: totalAmountToPay,
					totalAmountToPay,
					status: "paid",
					dateOfPayment: new Date().toISOString(),
				},
			],
			vehicleRegDetails: { ...leaseDataToUpdate?.vehicleRegDetails },
		};

		try {
			setLoading(true);
			const response = await investorRegisterAsset(leaseDataToUpdate);

			if (leaseData?.assetObj) {
				const resTwo = await onUpdateAsset(leaseData?.assetObj?.id, assetDataToUpdate);

				console.log(resTwo);
			}

			if (response) {
				toast.success("Payment has been deducted successfully for vehicle registration");

				setTimeout(() => {
					window.location.reload();
				}, 2000);
			} else {
				throw Error("Error updating Object!");
			}
		} catch (err) {
			console.log(err);
			toast.error(err?.error || "Error updating Object!");
		} finally {
			setLoading(false);
		}
	};

	const handleSubmitRegNo = async (e) => {
		e.preventDefault();

		if (miniForm?.registrationStatus === "") {
			toast.error("Please update registration status");
			return;
		}
		if (miniForm?.registrationStatus === "Registered" && !miniForm?.registrationNum) {
			toast.error("Please update vehicle registration number");
			return;
		}
		if (
			miniForm?.registrationStatus &&
			miniForm?.registrationStatus !== "Registered" &&
			miniForm?.registrationNum
		) {
			toast.error("Invalid status selected. Please select (Registered)");
			setTimeout(() => {
				setMiniForm({
					...miniForm,
					registrationNum: leaseData?.vehicleRegDetails?.registrationNum || "",
				});
			}, 1000);
			return;
		}

		const data = {
			vehicleRegDetails: {
				...leaseData?.vehicleRegDetails,
				registrationStatus: miniForm?.registrationStatus,
				registrationNum: miniForm?.registrationNum?.toUpperCase(),
				registrationDate: miniForm?.registrationNum !== "" ? new Date() : "",
			},
		};

		try {
			setLoading(true);
			const response = await updateLogObject(leaseData?.objectId, data, "lease");

			if (leaseData?.assetObj) {
				const resTwo = await onUpdateAsset(leaseData?.assetObj?.id, data);

				console.log(resTwo);
			}

			if (response) {
				toast.success(
					`Registration ${miniForm?.registrationNum !== "" ? "Number" : "Staus"} updated successfully`
				);

				setTimeout(() => {
					window.location.reload();
				}, 2000);
			} else {
				throw Error("Error updating Object!");
			}
		} catch (err) {
			console.log(err);
			toast.error(err?.error || "Error updating Object!");
		} finally {
			setLoading(false);
		}

		console.log(data, leaseData);
	};

	useEffect(() => {
		if (regAssetModal) {
			document.body.style.overflow = "hidden";
		}
		return () => {
			// eslint-disable-next-line no-undef
			document.body.style.overflow = "auto";
		};
	}, [regAssetModal]);

	return (
		<>
			<Backdrop sx={{ zIndex: 13 }} open={regAssetModal}>
				<RegisterAssetModal
					activeTab={activeTab}
					setActiveTab={setActiveTab}
					onCloseModal={() => setRegAssetModal(false)}
					amountPayable={totalAmountToPay}
					handleRegisterAsset={onAdminRegisterAsset}
				/>
			</Backdrop>

			{loading && (
				<Backdrop sx={{ color: "#fff", zIndex: 10 }} open={loading}>
					<CircularProgress color='inherit' />
				</Backdrop>
			)}

			<div style={{ position: "relative" }}>
				<CustomSmallBtn
					style={{
						position: "absolute",
						left: "-20px",
						top: "-115px",
						background: "white",
						color: "#006770",
					}}
					onClick={() => navigate(-1)}>
					<FaArrowLeft />
					<span>Go Back</span>
				</CustomSmallBtn>
				<StyledForm onSubmit={handleSubmitRegNo}>
					<AssetRegistrationSideBar
						leaseData={leaseData}
						toggleEdit={toggleEdit}
						handleCancel={handleCancel}
						setToggleEdit={setToggleEdit}
						isEditable={isSaveAssetRegNumberReady()}
					/>
					<div className='RightSection'>
						<FormAccordion title='Asset Registration Information' open={true}>
							<AssetRegistrationInfo
								formData={leaseData}
								toggleEdit={toggleEdit}
								handleClick={onBeginAssetRegPayment}
								canRegisterAsset={isAssetRegistrationReady()}
								miniForm={miniForm}
								handleChange={handleChange}
							/>
						</FormAccordion>
						<FormAccordion title='Lease Information'>
							<CompletedAssetInformation formData={leaseData} />
						</FormAccordion>
						<FormAccordion title='Product Information'>
							<CompletedAssetProductInfo formData={leaseData} />
						</FormAccordion>
					</div>
				</StyledForm>
			</div>
		</>
	);
};

AssetRegistration.propTypes = {
	leaseData: PropTypes.object,
};

export default AssetRegistration;
